/* eslint-disable complexity */
/* eslint-disable max-statements */
import React from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cx from 'clsx'
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme'
import { ThemeProvider } from '@emotion/react'
import { FullPageSpinner } from '../utils'
import { serverData } from '../../utils/server-data'
import { injectWorldReady } from '@paypalcorp/worldready-react'
import IntlProvider from '@/hooks/intl/IntlProvider'
import { css } from '@emotion/core'
import { hideNavBarCss, showNavBarCss, modalCss, modalContainerCss, ppVxModalCss } from './styles'
import { StepperType } from 'components/stepper'
import { ieCss } from '../common/style'
import ModalNavBar from './ModalNavBar'
import { WEBVIEW_CALLBACK_NAMES, triggerVeniceCallback } from 'utils/mobile'
import '@paypalcorp/pp-vx/dist/components/modal/modal.css'
import '@paypalcorp/pp-vx/dist/core/core.js'
import '@paypalcorp/pp-vx/dist/components/modal/modal.js'
import { getErrorHandlerData } from 'utils/error/error-handler'
import {
  selectModalTitleContent,
  selectShowModalPageSpinner,
  selectModalUnexpectedClientError,
  selectModalRetryFetchName,
} from '@/features/ui/ui-slice'
import { isPayrollPartnerVariantOrIntent } from '../../utils/flow'

const { UCS_FLOW_ERROR } = WEBVIEW_CALLBACK_NAMES

const isShowBackButton = ({
  stepper,
  retryFetchName,
  terminalSteps,
  nonTerminalSteps,
  loadingSteps,
  onFirstStepBack,
  showModalPageSpinner,
}) => {
  const hideBackButtonOnTerminalStep = terminalSteps.includes(stepper.getCurrentStepName())
  const showBackButtonInBetweenFirstAndLastStep = !stepper.isFirstStep() && !stepper.isLastStep()
  const showBackButtonInNonTerminalStep = nonTerminalSteps.includes(stepper.getCurrentStepName())
  const showBackButtonInFirstStep = onFirstStepBack && stepper.isFirstStep()
  const hideBackButtonOnLoadingStep = loadingSteps.includes(stepper.getCurrentStepName())

  return (
    !showModalPageSpinner &&
    !retryFetchName &&
    !hideBackButtonOnTerminalStep &&
    !hideBackButtonOnLoadingStep &&
    (showBackButtonInFirstStep ||
      showBackButtonInBetweenFirstAndLastStep ||
      showBackButtonInNonTerminalStep)
  )
}

function FlowModal({
  stepper,
  worldReady,
  onClose,
  onFirstStepBack,
  hideContent,
  flowName,
  terminalSteps = [],
  nonTerminalSteps = [],
  loadingSteps = [],
  children,
  modalOverrideCss = {},
  navBarOverrideCss = {},
  closeDisabled = false,
  ...restProps
}) {
  // v1 UI state
  const isSessionExpired = useSelector((state: any) => state.common.isSessionExpired)
  const isRequestBlocked = useSelector((state: any) => state.common.isRequestBlocked)
  const loginUrl = useSelector((state: any) => state.common.loginUrl)

  // v2 UI state
  const modalTitleContent = useSelector(selectModalTitleContent)
  const showModalPageSpinner = useSelector(selectShowModalPageSpinner)
  const modalUnexpectedClientError = useSelector(selectModalUnexpectedClientError)
  const modalRetryFetchName = useSelector(selectModalRetryFetchName)

  const { fetchRetried, retryHandler } = useSelector(
    (state: any) => state.ui.fetches[modalRetryFetchName] || {},
  )

  const { errorType, errorContent } = getErrorHandlerData({
    flowName,
    isSessionExpired,
    isRequestBlocked,
    loginUrl,
    fetchRetried,
    retryHandler,
    retryFetchName: modalRetryFetchName,
    unexpectedClientError: modalUnexpectedClientError,
    errorPageProps: restProps,
  })

  if (errorType && serverData.isMobileNative) {
    triggerVeniceCallback(UCS_FLOW_ERROR, {
      errorType,
      flowName,
    })
  }

  if (!stepper) {
    // For single page render, such as UnexpectedErrorPage
    stepper = {
      isFirstStep: () => {
        return true
      },
      isLastStep: () => {
        return true
      },
      getCurrentStepName: () => {
        return ''
      },
    }
  }

  // * Show back button for second page onwards unless onFirstStepBack option is passed
  // * Hide back button when Retry or Try again later error page is shown
  // * Hide back button on last page unless it is a non terminal page
  // * Hide back button for a terminal page
  const showBackButton = isShowBackButton({
    stepper,
    retryFetchName: modalRetryFetchName,
    terminalSteps,
    nonTerminalSteps,
    loadingSteps,
    onFirstStepBack,
    showModalPageSpinner,
  })

  const hideCloseButtonBasedOnIntent = isPayrollPartnerVariantOrIntent()

  const showTitle = !serverData.isMobileNative
  const hideCloseButton =
    serverData.isMobileNative ||
    loadingSteps.includes(stepper.getCurrentStepName()) ||
    hideCloseButtonBasedOnIntent
  const hideNavBar = !showBackButton && hideCloseButton && !hideCloseButtonBasedOnIntent
  const navBarCss = hideNavBar ? hideNavBarCss : showNavBarCss
  const content = errorContent || children

  return (
    <div css={css([modalCss, ppVxModalCss, modalOverrideCss])}>
      <IntlProvider worldReady={worldReady}>
        <ThemeProvider theme={PAYPAL_THEME}>
          {/* TODO To use pp-react Overpanel Modal once bugs has been resolved in the latest version */}
          <article
            className={cx('ppvx_modal', 'ppvx_modal--overpanel', {
              'papui_flow_modal--show_navbar': !hideNavBar,
            })}
            css={css([modalContainerCss, ieCss])}
          >
            {showModalPageSpinner && <FullPageSpinner />}
            {!hideNavBar && (
              <ModalNavBar
                titleContent={modalTitleContent}
                stepper={stepper}
                onClose={onClose}
                onFirstStepBack={onFirstStepBack}
                hideCloseButton={hideCloseButton}
                showBackButton={showBackButton}
                showTitle={showTitle}
                closeDisabled={closeDisabled}
              />
            )}
            <section
              id="modal-contents"
              className="ppvx_modal__contents"
              css={css([navBarCss, navBarOverrideCss])}
            >
              {!hideContent && content}
            </section>
          </article>
        </ThemeProvider>
      </IntlProvider>
    </div>
  )
}

FlowModal.propTypes = {
  stepper: StepperType,
  worldReady: PropTypes.object,
  onClose: PropTypes.func,
  onFirstStepBack: PropTypes.func,
  hideContent: PropTypes.bool,
  children: PropTypes.any,
  flowName: PropTypes.string,
  terminalSteps: PropTypes.array,
  nonTerminalSteps: PropTypes.array,
  loadingSteps: PropTypes.array,
  modalOverrideCss: PropTypes.object,
  navBarOverrideCss: PropTypes.object,
  closeDisabled: PropTypes.bool,
}

export default injectWorldReady(FlowModal)
